import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api, formatApiError, gbp } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { inputClass } from "@/lib/constants";

const NEXT_STATES = {
  confirmed: ["in_preparation"],
  in_preparation: ["ready_for_collection", "out_for_delivery"],
  ready_for_collection: ["completed"],
  out_for_delivery: ["completed"],
  requires_attention: ["confirmed", "cancelled"],
};
const LABELS = {
  in_preparation: "Start preparation", ready_for_collection: "Ready for collection",
  out_for_delivery: "Out for delivery", completed: "Mark completed", confirmed: "Confirm order", cancelled: "Cancel order",
};

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [restoreStock, setRestoreStock] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [newToken, setNewToken] = useState("");

  const load = () => api.get(`/admin/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
  useEffect(() => { load(); }, [id]);

  const act = (fn, msg) => fn().then(() => { toast.success(msg); load(); }).catch((e) => toast.error(formatApiError(e)));

  if (order === null) return <p className="text-sm text-cream-muted">Loading…</p>;
  if (order === false) return <p className="text-sm text-red-400">Order not found.</p>;

  const nextStates = NEXT_STATES[order.order_status] || [];
  const canCancel = ["awaiting_payment", "confirmed", "in_preparation", "ready_for_collection", "out_for_delivery"].includes(order.order_status);
  const productionStarted = ["in_preparation", "ready_for_collection", "out_for_delivery", "completed"].includes(order.order_status);

  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin/orders" className="text-xs font-bold text-gold hover:underline" data-testid="back-to-orders">← Orders</Link>
      <Link to={`/admin/production/packing/${id}`} className="ml-4 text-xs font-bold text-gold hover:underline" data-testid="order-packing-sheet-link">Packing sheet →</Link>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="font-serif text-2xl font-bold text-cream-text" data-testid="order-detail-number">{order.order_number}</h1>
        {order.order_status === "requires_attention" && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 px-3 py-1 text-xs font-bold text-red-300" data-testid="requires-attention-badge">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Requires attention
          </span>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="order-items-section">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Items</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {order.items.map((item, i) => (
                <li key={i} className="flex justify-between gap-3 text-cream-muted">
                  <span>{item.qty} × {item.name}
                    {Object.keys(item.option_values || {}).length > 0 && <span className="block text-xs">{Object.entries(item.option_values).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>}
                    {item.allergen_declaration_status === "none_of_14_as_ingredients"
                      ? <span className="block text-xs text-amber-400/80">No UK 14 regulated allergens used as ingredients</span>
                      : item.allergens?.length > 0 && <span className="block text-xs capitalize text-amber-400/80">Allergens: {item.allergens.join(", ")}</span>}
                    {item.may_contain?.length > 0 && <span className="block text-xs capitalize text-amber-300/80">May contain: {item.may_contain.join(", ")}</span>}
                  </span>
                  <span className="text-cream-text">{gbp(item.line_total_pence)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-1 border-t border-choco-line pt-3 text-sm text-cream-muted">
              <p className="flex justify-between"><span>Subtotal</span><span>{gbp(order.subtotal_pence)}</span></p>
              <p className="flex justify-between"><span>{order.fulfilment_method === "delivery" ? "Delivery" : "Collection"}</span><span>{gbp(order.delivery_fee_pence)}</span></p>
              <p className="flex justify-between font-bold text-cream-text"><span>Total</span><span>{gbp(order.total_pence)}</span></p>
            </div>
          </section>

          <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="order-customer-section">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Customer &amp; fulfilment</h2>
            <div className="mt-3 space-y-1 text-sm text-cream-muted">
              <p>{order.customer.name} · {order.customer.email} · {order.customer.phone}</p>
              {order.address_snapshot && (
                <p>{order.address_snapshot.line1}{order.address_snapshot.line2 ? `, ${order.address_snapshot.line2}` : ""}, {order.address_snapshot.city}, {order.address_snapshot.postcode}</p>
              )}
              {order.slot_snapshot && <p className="capitalize">{order.fulfilment_method}: {order.slot_snapshot.date} {order.slot_snapshot.start_time}–{order.slot_snapshot.end_time}</p>}
              {order.delivery_zone_snapshot && <p>Zone: {order.delivery_zone_snapshot.name} ({gbp(order.delivery_zone_snapshot.fee_pence)})</p>}
              {order.customer_notes && <p className="mt-2 rounded-xl bg-choco p-3 text-xs">Notes: {order.customer_notes}</p>}
              {order.marketing_consent && <p className="text-xs">Marketing consent: yes</p>}
              <p className="text-xs">Allergen acknowledgement: {order.allergen_acknowledged ? "yes" : "no"}</p>
            </div>
          </section>

          <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="order-history-section">
            <h2 className="font-serif text-lg font-semibold text-cream-text">History</h2>
            <ul className="mt-3 space-y-2" data-testid="order-history-list">
              {(order.status_history || []).map((h, i) => (
                <li key={i} className="text-xs text-cream-muted">
                  <span className="font-semibold text-cream-text">{h.field}</span>: {String(h.previous ?? "—")} → <span className="font-semibold text-gold">{String(h.new)}</span>
                  {" · "}{h.reason} · {h.actor_type}{h.actor_id ? ` (${h.actor_id.slice(0, 6)}…)` : ""} · {new Date(h.at).toLocaleString("en-GB", { timeZone: "Europe/London" })}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="order-status-section">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Status</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-cream-muted">Order</dt><dd className="font-bold capitalize text-cream-text" data-testid="order-status-value">{order.order_status.replace(/_/g, " ")}</dd></div>
              <div className="flex justify-between"><dt className="text-cream-muted">Payment</dt><dd className="font-bold capitalize text-cream-text" data-testid="payment-status-value">{order.payment_status.replace(/_/g, " ")}</dd></div>
              <div className="flex justify-between"><dt className="text-cream-muted">Reservation</dt><dd className="capitalize text-cream-text">{order.reservation_status}</dd></div>
              {order.reservation_expires_at && <div className="flex justify-between"><dt className="text-cream-muted">Reservation expires</dt><dd className="text-cream-text">{new Date(order.reservation_expires_at).toLocaleString("en-GB", { timeZone: "Europe/London" })}</dd></div>}
              {order.release_reason && <div className="flex justify-between gap-2"><dt className="text-cream-muted">Release</dt><dd className="text-right text-xs text-cream-text">{order.release_reason}</dd></div>}
            </dl>

            <div className="mt-5 space-y-2">
              {order.payment_status === "unpaid_on_collection" && (
                <button onClick={() => act(() => api.post(`/admin/orders/${order.id}/mark-paid`), "Marked as paid on collection")} className="w-full rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="mark-paid-button">
                  Mark paid on collection ({gbp(order.total_pence)})
                </button>
              )}
              {nextStates.filter((s) => s !== "cancelled").map((s) => (
                <button key={s} onClick={() => act(() => api.post(`/admin/orders/${order.id}/transition`, { to: s }), LABELS[s])} className="w-full rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`transition-${s}`}>
                  {LABELS[s]}
                </button>
              ))}
              {order.stripe_payment_intent_id && (
                <button onClick={() => act(() => api.post(`/admin/orders/${order.id}/reconcile`), "Reconciled with Stripe")} className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="reconcile-button">
                  <RefreshCw className="h-4 w-4" aria-hidden="true" /> Reconcile payment
                </button>
              )}
              {canCancel && !showCancel && (
                <button onClick={() => setShowCancel(true)} className="w-full rounded-full border border-red-500/40 px-5 py-2.5 text-sm font-bold text-red-400 hover:bg-red-500/10" data-testid="cancel-order-button">
                  Cancel order
                </button>
              )}
              {showCancel && (
                <div className="space-y-3 rounded-2xl border border-red-500/40 bg-red-500/5 p-4" data-testid="cancel-panel">
                  {productionStarted && (
                    <p className="text-xs font-semibold text-amber-300">Production has started — choose whether stock should be restored.</p>
                  )}
                  <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason" className={inputClass} data-testid="cancel-reason-input" />
                  <div className="flex items-center gap-2">
                    <Checkbox id="restore-stock" checked={restoreStock} onCheckedChange={(v) => setRestoreStock(v === true)} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid="restore-stock-checkbox" />
                    <label htmlFor="restore-stock" className="text-xs text-cream-muted">Restore stock &amp; slot capacity</label>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => act(() => api.post(`/admin/orders/${order.id}/cancel`, { reason: cancelReason, restore_stock: restoreStock }), "Order cancelled").then(() => setShowCancel(false))} className="rounded-full bg-red-500 px-4 py-2 text-xs font-bold text-white hover:bg-red-600" data-testid="confirm-cancel-button">
                      Confirm cancel
                    </button>
                    <button onClick={() => setShowCancel(false)} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-muted" data-testid="abort-cancel-button">Keep order</button>
                  </div>
                </div>
              )}
              <button
                onClick={() => act(async () => {
                  const res = await api.post(`/admin/orders/${order.id}/rotate-access-token`);
                  setNewToken(res.data.access_token);
                }, "Customer access link rotated")}
                className="w-full rounded-full border border-choco-line px-5 py-2.5 text-xs font-bold text-cream-muted hover:border-gold hover:text-gold"
                data-testid="rotate-token-button"
              >
                Rotate customer access link
              </button>
              {newToken && (
                <p className="break-all rounded-xl bg-choco p-3 text-xs text-gold" data-testid="rotated-token">
                  {window.location.origin}/order/{order.order_number}?t={newToken}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
